import React, { createContext, useState, useEffect, useContext } from "react";
import { translations } from "@/lib/translation"; // ✅ external translations file
import { translationsExtra } from "@/lib/translationsExtra";

export type Language = "en" | "am" | "fr" | "ar" | "es" | "zh-CN" | "hi";

interface LanguageContextType {
    language: Language;
    setLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
const translationsExtraMap = translationsExtra as Record<string, Partial<Record<Language, string>>>;
const translationAliases: Record<string, string> = {
    minCelebrationVote: "Min Celebration Vote",
    suggestionLimit: "Max Suggestions",
    noPlaylistsFound: "No Playlists Found",
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [language, setLanguageState] = useState<Language>("en");

    useEffect(() => {
        const saved = localStorage.getItem("qit-language") as Language | null;
        if (saved && isValidLanguage(saved)) {
            setLanguageState(saved);
        }
    }, []);

    const setLanguage = (lang: Language) => {
        setLanguageState(lang);
        localStorage.setItem("qit-language", lang);
    };

    return (
        <LanguageContext.Provider value={{ language, setLanguage }}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = (): LanguageContextType & { t: (key: string) => string } => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error("useLanguage must be used within a LanguageProvider");
    }

    const t = (key: string): string => {
        const resolvedKey = translationAliases[key] || key;
        return (
            translations[context.language]?.[resolvedKey]
            || translationsExtraMap[resolvedKey]?.[context.language]
            || translations["en"]?.[resolvedKey]
            || translationsExtraMap[resolvedKey]?.en
            || resolvedKey
        );
    };

    return { ...context, t };
};

const isValidLanguage = (lang: any): lang is Language =>
    ["en", "am", "fr", "ar", "es", "zh-CN", "hi"].includes(lang);
